import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { EventService } from "services/event.service";
import { IEventResponse } from "models/church.types";

export default function EventList() {
  const [events, setEvents] = useState<IEventResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 8;

  const loadData = async (query: string, page: number) => {
    setLoading(true);
    try {
      const result = query.trim()
        ? await EventService.search(query, page, itemsPerPage)
        : await EventService.getAll(page, itemsPerPage);
      setEvents(result.data);
      setTotalItems(result.totalCount);
    } catch (error) {
      console.error("Erreur chargement événements:", error);
      toast.error("Impossible de charger les événements");
    } finally {
      setLoading(false);
    }
  };

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    [],
  );

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Supprimer cet événement ? Cela supprimera également toutes les présences rattachées.",
      )
    ) {
      setLoading(true);
      try {
        await EventService.delete(id);
        toast.success("Événement supprimé avec succès");
        loadData(searchTerm, currentPage);
      } catch (error) {
        toast.error(
          "Suppression impossible, cet événement est concerné par un traitement",
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const onSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedSearch(value);
  };

  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage]);

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">
      {/* TITRE PRINCIPAL */}
      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight">
          Registre des Événements
        </h2>
        <p className="text-slate-400 text-sm italic">
          Planification, suivi opérationnel et historique des activités de
          l'église
        </p>
      </div>

      {/* BARRE D'OUTILS */}
      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">
        <div className="relative w-full md:w-96 group">
          <span className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-slate-400 group-focus-within:text-[#D4AF37] transition-colors">
            {loading ? (
              <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
            ) : (
              <i className="fas fa-calendar-alt"></i>
            )}
          </span>
          <input
            type="text"
            value={searchTerm}
            onChange={onSearchChange}
            placeholder="Rechercher un événement..."
            className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
          />
        </div>

        <Link
          to="/admin/events/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm"
        >
          <i className="fas fa-plus-circle mr-2 text-base"></i> Nouvel Événement
        </Link>
      </div>

      {/* TABLE CARD */}
      <div className="relative flex flex-col min-w-0 break-words w-full mb-10 shadow-2xl rounded-2xl bg-slate-800 border border-slate-700 overflow-hidden">
        <div className="block w-full overflow-x-auto">
          <table className="items-center w-full bg-transparent border-collapse text-left">
            <thead>
              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">
                <th className="px-6 py-5">Événement / Type</th>
                <th className="px-6 py-5">Date de l'événement</th>
                <th className="px-6 py-5 text-center">Créé par</th>
                <th className="px-6 py-5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700/50 text-slate-300">
              {events.length === 0 && !loading ? (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >
                    <i className="fas fa-calendar-times block text-3xl mb-3 opacity-20"></i>
                    Aucun événement enregistré.
                  </td>
                </tr>
              ) : (
                events.map((event) => (
                  <tr
                    key={event.id}
                    className="hover:bg-slate-700/40 transition-colors group"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-lg bg-slate-900 flex items-center justify-center text-[#D4AF37] font-bold mr-4 border border-slate-700">
                          <i className="fas fa-bookmark text-xs"></i>
                        </div>
                        <div className="flex flex-col">
                          <div className="text-sm font-bold text-white uppercase tracking-tight">
                            {event.title || "Sans titre"}
                          </div>
                          <div className="text-[10px] text-[#D4AF37] font-semibold uppercase tracking-wider mt-0.5">
                            {event.eventTypeName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col">
                        <span className="text-sm text-white font-medium">
                          {event.eventDate
                            ? new Date(event.eventDate).toLocaleDateString(
                                "fr-FR",
                                {
                                  weekday: "long",
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                },
                              )
                            : "N/A"}
                        </span>
                        <span className="text-[10px] text-slate-500 uppercase font-bold">
                          À{" "}
                          {event.eventDate
                            ? new Date(event.eventDate).toLocaleTimeString(
                                "fr-FR",
                                { hour: "2-digit", minute: "2-digit" },
                              )
                            : "00:00"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-[10px] font-medium text-center uppercase whitespace-nowrap">
                      <span className="bg-slate-900/50 px-3 py-1.5 rounded-md text-slate-400 border border-slate-700/40">
                        {event.createdBy || "Système"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right global-actions">
                      <div className="flex justify-end gap-5">
                        <Link
                          to={`/admin/events/edit/${event.id}`}
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                          title="Modifier"
                        >
                          <i className="fas fa-edit text-lg"></i>
                        </Link>
                        <button
                          onClick={() => handleDelete(event.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Supprimer"
                        >
                          <i className="fas fa-trash-alt text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">
          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">
            Total : <span className="text-[#D4AF37]">{totalItems}</span>{" "}
            événements
          </p>
          <div className="flex gap-2">
            <button
              disabled={currentPage === 0}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>
            <button
              disabled={currentPage + 1 >= totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
